import { useState, useCallback } from 'react'
import {
  CheckCircleFilled,
  CloseCircleFilled,
  FilePdfOutlined,
  EditOutlined,
} from '@ant-design/icons'
import { Button, Descriptions, Divider, message, Popconfirm } from 'antd'
import { createUseStyles, useTheme } from 'react-jss'
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom'
import api from '../../../api'
import { useAsync } from '../../../hooks'
import { formatDateTime, formatDOB } from '../../../lib/datetime'
import { toPath } from '../../../routes'
import {
  DESCRIPTIONS_TWO_COLUMNS,
  MOBILE_MEDIA_QUERY,
} from '../../../lib/responsive'
import Comments from '../../Comments'
import { ListError, PageHeader } from '../../Common'
import Loading from '../../Loading'
import DownloadLink from '../../Common/DownloadLink'
import InsuranceAuthorizationModal from '../../PreAuthorizations/InsuranceAuthorizationModal'
import filenameFromBlobPath from '../../../lib/active-storage'

const useStyles = createUseStyles(theme => ({
  container: {
    [MOBILE_MEDIA_QUERY]: {
      // Keep the last section clear of the fixed actions bar.
      paddingBottom: 80,
    },
  },
  actions: {
    marginTop: 30,
    textAlign: 'center',
    [MOBILE_MEDIA_QUERY]: {
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      padding: 12,
      backgroundColor: '#fff',
      borderTop: 'solid 1px #f0f0f0',
      gap: 8,
      zIndex: 100,
      display: 'flex',
      flexDirection: 'column-reverse',
      marginTop: 0,
      textAlign: 'right',
      // Not flex: 1 — the bar stacks, so along the main axis that stretches the
      // buttons vertically and eats their own padding. Full width is what was
      // wanted, and a Link wrapper does not pass that to the button inside it.
      '& > *': {
        width: '100%',
      },
      '& button': {
        width: '100%',
      },
    },
  },
  pdfIcon: {
    fontSize: '32px',
    color: theme.grayPrimary,
  },
}))

export default function Details() {
  const { brandSuccess, brandDanger } = useTheme()
  const [insuranceAuthVisible, setInsuranceAuthVisible] = useState(false)
  const navigate = useNavigate()
  const { id } = useParams()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const practiceId = searchParams.get('practiceId')
  const classes = useStyles()
  const getPreAuthorization = useCallback(
    () => api.preAuthorizations.get(id),
    [id],
  )
  const {
    pending,
    value: preAuthorization,
    error,
    clearError,
    execute: revalidatePreAuth,
  } = useAsync(getPreAuthorization)

  const insuranceAuth = preAuthorization && {
    insuranceAuthorizationNumber: preAuthorization.insuranceAuthorizationNumber,
    notes: preAuthorization.notes,
    notesFilePath: preAuthorization.notesFilePath,
    insuranceAuthorizationFilePath:
      preAuthorization.insuranceAuthorizationFilePath,
  }

  const handleDelete = useCallback(async () => {
    try {
      await api.preAuthorizations.destroy(id)
      message.success('Remote authorization successfully removed')
      navigate(toPath.practices.officeAuthorizations({ practiceId }))
    } catch (e) {
      message.error(
        `There was an error removing this remote authorization: ${e.message}`,
      )
    }
  }, [id, navigate, practiceId])

  const onFinishInsuranceAuthModal = useCallback(
    (insuranceAuthChanged = false) => {
      setInsuranceAuthVisible(false)
      if (insuranceAuthChanged) {
        revalidatePreAuth()
      }
    },
    [revalidatePreAuth],
  )

  if (pending) return <Loading large />
  if (error) {
    return <ListError description={error.message} onClose={clearError} />
  }
  const { device, patient, assignee } = preAuthorization

  return (
    <div className={classes.container}>
      <PageHeader
        title="Office Authorization"
        subTitle={`${patient.firstName} ${patient.lastName}`}
        onBack={() => navigate(-1)}
      />
      <Divider orientation="left">Personal Information</Divider>
      <Descriptions column={DESCRIPTIONS_TWO_COLUMNS} size="small">
        <Descriptions.Item label="First Name">
          {patient.firstName}
        </Descriptions.Item>
        <Descriptions.Item label="Last Name">
          {patient.lastName}
        </Descriptions.Item>
        <Descriptions.Item
          label="Date of birth"
          span={DESCRIPTIONS_TWO_COLUMNS}
        >
          {formatDOB(patient.birthDate)}
        </Descriptions.Item>
      </Descriptions>
      <div>
        <Divider orientation="left">Insurance Information</Divider>
        <Descriptions column={DESCRIPTIONS_TWO_COLUMNS} size="small">
          <Descriptions.Item label="Insurance Provider">
            {patient.insuranceProvider ? patient.insuranceProvider.name : ''}
          </Descriptions.Item>
          <Descriptions.Item label="Insurance Policy #">
            {patient.insurancePolicyNumber}
          </Descriptions.Item>
        </Descriptions>
        <Divider orientation="left">Insurance Authorization:</Divider>
        <Descriptions column={DESCRIPTIONS_TWO_COLUMNS} size="small">
          <Descriptions.Item label="Insurance Auth file">
            {preAuthorization.insuranceAuthorizationFilePath && (
              <DownloadLink
                path={preAuthorization.insuranceAuthorizationFilePath}
              >
                <FilePdfOutlined
                  className={classes.pdfIcon}
                  aria-label="insurance authorization file"
                />
              </DownloadLink>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Insurance Auth #">
            {preAuthorization.insuranceAuthorizationNumber}
          </Descriptions.Item>
          <Descriptions.Item label="Notes" span={DESCRIPTIONS_TWO_COLUMNS}>
            {preAuthorization.notesFilePath ? (
              <DownloadLink path={preAuthorization.notesFilePath}>
                <span>
                  {preAuthorization.notes ||
                    filenameFromBlobPath(preAuthorization.notesFilePath)}
                </span>
              </DownloadLink>
            ) : (
              preAuthorization.notes
            )}
          </Descriptions.Item>
        </Descriptions>
        <Divider orientation="left">Medical Providers</Divider>
        <Descriptions column={DESCRIPTIONS_TWO_COLUMNS} size="small">
          <Descriptions.Item label="Provider">
            {patient.provider}
          </Descriptions.Item>
          <Descriptions.Item label="PCP name">
            {patient.pcpName}
          </Descriptions.Item>
          <Descriptions.Item label="PCP phone number">
            {patient.pcpPhoneNumber}
          </Descriptions.Item>
          <Descriptions.Item label="PCP fax number">
            {patient.pcpFaxNumber}
          </Descriptions.Item>
        </Descriptions>
        <Divider orientation="left">Cardiac Device</Divider>
        <Descriptions column={DESCRIPTIONS_TWO_COLUMNS} size="small">
          <Descriptions.Item label="Company">
            {device.deviceModel.manufacturer}
          </Descriptions.Item>
          <Descriptions.Item label="Type">
            {device.deviceModel.deviceType.name}
          </Descriptions.Item>
          <Descriptions.Item label="Serial Number">
            {device.serialNumber}
          </Descriptions.Item>
          <Descriptions.Item label="CPT Code">
            {preAuthorization.cptCode}
          </Descriptions.Item>
          <Descriptions.Item label="ICD-10 Code">
            {preAuthorization.icd10Code}
          </Descriptions.Item>
        </Descriptions>
        <Divider orientation="left">More</Divider>
        <Descriptions column={DESCRIPTIONS_TWO_COLUMNS} size="small">
          <Descriptions.Item label="Authorized">
            {preAuthorization.authorized ? (
              <CheckCircleFilled style={{ color: brandSuccess }} />
            ) : (
              <CloseCircleFilled style={{ color: brandDanger }} />
            )}
            &nbsp;
            {preAuthorization.authorized ? 'Yes' : 'No'}
          </Descriptions.Item>
          <Descriptions.Item label="Date of Check">
            {preAuthorization.billingDate
              ? formatDateTime(preAuthorization.billingDate)
              : '-'}
          </Descriptions.Item>
        </Descriptions>
        {assignee && (
          <>
            <Divider orientation="left">Clinician</Divider>
            <Descriptions column={DESCRIPTIONS_TWO_COLUMNS} size="small">
              <Descriptions.Item label="Name">
                {assignee.lastName}, {assignee.firstName}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {assignee.email}
              </Descriptions.Item>
            </Descriptions>
          </>
        )}
      </div>

      {/* TODO: use ActionButtons */}
      <div className={classes.actions}>
        <Button
          icon={<FilePdfOutlined />}
          onClick={() => setInsuranceAuthVisible(true)}
        >
          {preAuthorization.insuranceAuthorizationFilePath ? 'Edit' : 'Add'}{' '}
          Authorization
        </Button>

        <Divider type="vertical" />
        <Link
          to={`${toPath.billing.officeAuthorizations.officeAuthorization.edit({
            id,
          })}?practiceId=${practiceId}`}
          className="edit-button"
        >
          <Button icon={<EditOutlined />} type="primary" ghost>
            Edit
          </Button>
        </Link>
        <Divider type="vertical" />
        <Popconfirm
          title="Are you sure?"
          okText="Yes"
          cancelText="No"
          onConfirm={handleDelete}
        >
          <Button
            danger
            icon={<CloseCircleFilled />}
            className="hard-delete-button"
          >
            Delete
          </Button>
        </Popconfirm>
      </div>

      <InsuranceAuthorizationModal
        preAuthorizationId={id}
        visible={insuranceAuthVisible}
        onFinish={onFinishInsuranceAuthModal}
        insuranceAuth={insuranceAuth}
      />
      <Comments collectionName="pre-authorizations" id={preAuthorization.id} />
    </div>
  )
}
